import { api } from '@/lib/api/client';
import type { QueuePriorityRules } from '@/features/admin/queue-priority/types';
import type { DoctorConsultationState, QueueItem, QueueStatus } from './types';

/**
 * GET /api/queue/priority-rules — this branch's Queue Prioritization rules,
 * read-only, plus whether the current user may change a patient's priority or
 * queue preferences (queue:priority). The server enforces that permission on
 * every write regardless; this only lets the Queue stage show the right
 * controls.
 */
export function fetchQueuePriorityRules(): Promise<QueuePriorityRules> {
  return api.get<QueuePriorityRules>('/queue/priority-rules');
}

/**
 * GET /api/queue/today — a bare array, no envelope. This is a pre-contract
 * (legacy) route, kept exactly as the backend defines it rather than
 * speculatively wrapped: HMS_V4_API_CONTRACT.md's paging decision explicitly
 * leaves this endpoint unpaged (bounded by the day already), so there is
 * nothing to `unwrap()` here.
 */
export function fetchTodayQueue(patientId?: number): Promise<QueueItem[]> {
  return api.get<QueueItem[]>('/queue/today', {
    query: patientId ? { patient_id: patientId } : undefined,
  });
}

/**
 * Moves a queue row. 'In Consultation' is a *guarded* transition server-side
 * — if the doctor is already with someone else it rejects with a 409
 * (`ApiError.code === 'conflict'`, `details.reason === 'doctor_busy'`) and
 * the row is not touched at all, so the patient stays Ready for Consultation.
 * See backend/utils/consultationState.js.
 *
 * 'On Hold' is not settable here on purpose — holding and resuming are the
 * two explicit calls below, so the audit trail can tell an interruption apart
 * from someone tapping through statuses.
 */
export function updateQueueStatus(id: number, status: Exclude<QueueStatus, 'On Hold'>, outOfOrderReason?: string): Promise<QueueItem> {
  // outOfOrderReason: required when starting a patient who is not next in
  // line (409 `details.reason === 'out_of_order'` otherwise) — see
  // OutOfOrderDialog.tsx and backend/utils/consultationState.js.
  return api.patch<QueueItem>(`/queue/${id}/status`, outOfOrderReason ? { status, out_of_order_reason: outOfOrderReason } : { status });
}

/** What this doctor is doing right now — the active consultation and anything they have on hold. */
export function fetchDoctorConsultationState(doctorId: number): Promise<DoctorConsultationState> {
  return api.get<DoctorConsultationState>(`/queue/doctor/${doctorId}/consultation-state`);
}

/** Pause a consultation in progress. Nothing already captured is lost — only the queue row's state moves. */
export function holdConsultation(id: number, reason?: string): Promise<QueueItem> {
  return api.post<QueueItem>(`/queue/${id}/hold`, reason ? { reason } : {});
}

/** 'On Hold' -> 'In Consultation', same doctor, same context. Refused with 'doctor_busy' if they picked up someone else. */
export function resumeConsultation(id: number): Promise<QueueItem> {
  return api.post<QueueItem>(`/queue/${id}/resume`, {});
}

/**
 * The controlled interruption: holds whatever the doctor is currently doing
 * and starts this Emergency-priority patient, in one server transaction.
 * Refused ('emergency_in_progress') when the doctor is already with another
 * emergency — that patient waits at the front of the emergency queue.
 */
export function startEmergencyConsultation(id: number): Promise<QueueItem> {
  return api.post<QueueItem>(`/queue/${id}/emergency-consultation`, {});
}
